'use client'

import {
  CheckCircle16,
  Clock16,
  Envelope16,
  LocationPin16,
  Umbrella16,
} from '@vinted/monochrome-icons'
import { Badge, Cell, Divider, Image, Rating, Spacer } from '@vinted/web-ui'
import { InView } from 'react-intersection-observer'

import { businessAccountFromUserDto } from '@marketplace-web/business-accounts/business-accounts-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { EMPTY_USER_IMAGE_NAME, useAsset } from '@marketplace-web/shared/assets'
import { UserTitle } from '@marketplace-web/common-components/user-title-ui'

import {
  ItemUserDto,
  profileClickEvent,
  viewEvent,
} from '@marketplace-web/user-follow/member-followers-data'
import FollowButton from '@marketplace-web/user-follow/follow-button-feature'

import { MEMBER_PROFILE_URL } from '../../constants/routes'
import UserInfoLine from './UserInfoLine'
import { getUserLocationDescription } from './utils'
import { isDateInTheFuture } from '../../utils/date'
import useDate from '../../hooks/useDate'

type Props = {
  user: ItemUserDto | null
  showActions: boolean
  testId?: string
}

const calculateRating = (reputation: number): number => {
  return Math.round(reputation * 5 * 100) / 100
}

const ItemUserInfo = ({ user, showActions = true, testId }: Props) => {
  const translate = useTranslate()
  const { formatRelativeTime } = useDate()
  const { track } = useTracking()
  const noPhotoAsset = useAsset('assets/no-photo')

  const isUKCompanyNumberHidden = useFeatureSwitch('hide_uk_sole_traders_company_number')
  const { screen, user: currentUser } = useSession()
  const businessAccount = businessAccountFromUserDto(user)

  const handleOnClick = () => {
    if (!user) return

    track(
      profileClickEvent({
        target: 'user_short_info_cell',
        ownerId: user.id.toString(),
      }),
    )
  }

  const renderHeader = () => {
    if (!user) return null

    const rating = calculateRating(user.feedback_reputation)

    return (
      <Cell
        prefix={
          <Image
            role="img"
            src={user.photo?.url || noPhotoAsset(EMPTY_USER_IMAGE_NAME)}
            styling={Image.Styling.Circle}
            size={Image.Size.Large}
          />
        }
        title={
          <UserTitle
            badgeTitle={translate('business.title')}
            businessAccountName={businessAccount?.name}
            login={user.login}
            isBusiness={user.business}
            isBody
            isBottomLoginHidden
            isBadgeHidden
          />
        }
        onClick={handleOnClick}
        url={MEMBER_PROFILE_URL(user.id)}
        type={Cell.Type.Navigating}
        testId={testId ? `${testId}-header` : undefined}
        chevron
      >
        <Rating
          aria={{
            'aria-label':
              (rating && translate('user.a11y.rating', { rating, max_rating: 5 })) || '',
          }}
          value={rating}
          text={user.feedback_count}
          size={Rating.Size.Small}
          emptyStateText={translate('user.feedback.no_reviews')}
        />
        {user.business && (
          <>
            <Spacer size={Spacer.Size.Small} />
            <Badge content={translate('business.title')} testId="seller-pro-badge" />
          </>
        )}
      </Cell>
    )
  }

  const renderBusinessEmail = () => {
    if (!user) return null

    const isBusinessAccount = user.business
    const businessEmail = isBusinessAccount && businessAccount?.email

    const hidePersonalInfo = isBusinessAccount && businessAccount?.entity_type === 'sole_trader'

    if (!businessEmail) return null

    if (businessEmail && hidePersonalInfo) {
      return (
        <UserInfoLine iconName={Envelope16} truncate>
          {businessEmail}
        </UserInfoLine>
      )
    }

    return (
      <UserInfoLine iconName={Envelope16} truncate>
        <a href={`mailto:${businessEmail}`} title={businessEmail} data-testid="seller-email-link">
          {businessEmail}
        </a>
      </UserInfoLine>
    )
  }

  const renderInfo = () => {
    if (!user) return null

    const isBusinessAccount = user.business
    const businessLegalCode = isBusinessAccount && businessAccount?.legal_code
    const hideBusinessLegalCode =
      isUKCompanyNumberHidden &&
      isBusinessAccount &&
      businessAccount?.entity_type === 'sole_trader' &&
      businessAccount?.country === 'UK'

    const handleLastSeenView = (inView: boolean) => {
      if (!inView) return

      track(
        viewEvent({
          screen,
          target: 'user_last_seen',
        }),
      )
    }

    return (
      <Cell testId={testId ? `${testId}-info` : undefined}>
        <SeparatedList separator={<Spacer size={Spacer.Size.Small} />}>
          <UserInfoLine iconName={LocationPin16}>{getUserLocationDescription(user)}</UserInfoLine>
          {renderBusinessEmail()}
          {businessLegalCode && !hideBusinessLegalCode && (
            <UserInfoLine iconName={CheckCircle16}>
              <span data-testid="seller-legal-code">
                {`${translate('user.verification.legal_code', {
                  count: user.msg_template_count,
                })} ${businessLegalCode}`}
              </span>
            </UserInfoLine>
          )}
          {user.is_on_holiday && (
            <UserInfoLine iconName={Umbrella16}>
              <span>{translate('user.on_holiday')}</span>
            </UserInfoLine>
          )}
          {user.last_loged_on_ts && (
            <InView onChange={handleLastSeenView}>
              <UserInfoLine iconName={Clock16}>
                <span suppressHydrationWarning>
                  {`${translate('user.last_seen')} ${formatRelativeTime(user.last_loged_on_ts)}`}
                </span>
              </UserInfoLine>
            </InView>
          )}
        </SeparatedList>
      </Cell>
    )
  }

  const renderActions = () => {
    if (!user) return null
    if (!currentUser || currentUser.id === user.id) return null
    if (isDateInTheFuture(user.closet_countdown_end_date)) return null

    return (
      <Cell testId={testId ? `${testId}-actions` : undefined}>
        <FollowButton userId={user.id} isActive={user.is_favourite} isFlat />
      </Cell>
    )
  }

  if (!user) return null

  return (
    <SeparatedList separator={<Divider />}>
      {renderHeader()}
      {renderInfo()}
      {showActions && renderActions()}
    </SeparatedList>
  )
}

export default ItemUserInfo
